import { AlertTriangle } from "lucide-react";
import { MONTHS, fmtSEK, g } from "./format";

// Build 8 (§14) — Group-only calculated section: customer pass-through per
// operating company. Read-only — derived from operating companies' canonical
// revenue; never editable here (no duplicate manual Group sources, §11).
export default function PassThroughSection({ passThrough }) {
  if (!passThrough) return null;
  const companies = passThrough.companies || [];
  return (
    <div className="mt-6" data-testid="rev-pass-through">
      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Calculated Customer Pass-through</h2>
        <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-[#EFF6FF] text-[#0047B3] border border-[#CFE3FC]">Read-only</span>
      </div>
      {(passThrough.warnings || []).length > 0 && (
        <div className="mb-2 space-y-1.5" data-testid="rev-pt-warnings">
          {passThrough.warnings.map((w, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {w}
            </div>
          ))}
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[1250px] border-collapse">
          <thead>
            <tr className="bg-secondary/50 border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3 sticky left-0 bg-secondary/50 min-w-56 z-10">Operating company</th>
              {MONTHS.map((label) => (
                <th key={label} className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-1 w-24">{label}</th>
              ))}
              <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-2 w-28">FY</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((c) => (
              <tr key={c.company_id} className="border-b border-slate-100 hover:bg-accent/40" data-testid={`rev-pt-row-${c.company_id}`}>
                <td className="py-2 px-3 sticky left-0 bg-white z-10">
                  <span className="text-sm font-semibold text-slate-800">{c.company_name}</span>
                  <span className="block text-[10px] text-slate-400">drill down via Intercompany workspace</span>
                </td>
                {MONTHS.map((_, i) => {
                  const val = g(c.months, i + 1)?.value;
                  return (
                    <td key={i + 1} className="py-2 px-1 text-right text-sm text-slate-700" data-testid={`rev-pt-cell-${c.company_id}-${i + 1}`}>
                      {val ? fmtSEK(val) : <span className="text-slate-300">—</span>}
                    </td>
                  );
                })}
                <td className="py-2 px-2 text-right text-sm font-bold text-slate-900" data-testid={`rev-pt-fy-${c.company_id}`}>{fmtSEK(c.fy)}</td>
              </tr>
            ))}
            <tr className="bg-secondary/50 border-t border-slate-200" data-testid="rev-pt-total-row">
              <td className="py-2 px-3 text-sm font-bold text-slate-900 sticky left-0 bg-secondary/50 z-10">Total pass-through</td>
              {MONTHS.map((_, i) => {
                const total = companies.reduce((sum, c) => sum + (g(c.months, i + 1)?.value || 0), 0);
                return (
                  <td key={i + 1} className="py-2 px-1 text-right text-sm font-bold text-slate-900">
                    {total ? fmtSEK(total) : <span className="text-slate-300">—</span>}
                  </td>
                );
              })}
              <td className="py-2 px-2 text-right text-sm font-bold text-slate-900" data-testid="rev-pt-fy-total">{fmtSEK(passThrough.fy)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
